import React, { useEffect, useState } from 'react';
import { HardDrive, Users, AlertTriangle, Database, RefreshCw } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { DashboardStats } from '../types';
import { formatBytes, getUsageColor } from '../utils/formatters';
import { TEXT } from '../constants/localization';
import AllCustomersCard from './AllCustomersCard';
import { API_ENDPOINTS } from '../config/api';
import { getDetailedErrorMessage, DetailedError } from '../utils/errorHandler';
import DetailedErrorDisplay from './DetailedErrorDisplay';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DetailedError | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    fetchDashboardStats();
    const interval = setInterval(fetchDashboardStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // 更新時間顯示，每30秒刷新一次
  useEffect(() => {
    const timeInterval = setInterval(() => {
      if (lastUpdated) {
        // 強制重新渲染以更新時間顯示
        setLastUpdated(new Date(lastUpdated));
      }
    }, 30000);
    return () => clearInterval(timeInterval);
  }, [lastUpdated]);

  const fetchDashboardStats = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      }
      const response = await apiClient.get<DashboardStats>(API_ENDPOINTS.DASHBOARD_STATS);
      setStats(response.data);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      const detailedError = getDetailedErrorMessage(err);
      setError(detailedError);
      console.error('Dashboard stats fetch error:', err);
    } finally {
      setLoading(false);
      if (isManualRefresh) {
        setRefreshing(false);
      }
    }
  };

  const handleManualRefresh = () => {
    fetchDashboardStats(true);
  };

  const getTimeAgo = (date: Date | null): string => {
    if (!date) return '';

    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 30) return TEXT.JUST_NOW;
    if (seconds < 60) return `${seconds} ${TEXT.SECONDS_AGO}`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} ${TEXT.MINUTES_AGO}`;

    const hours = Math.floor(minutes / 60);
    return `${hours} 小時前`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 transition-colors">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{TEXT.DASHBOARD}</h1>
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? TEXT.REFRESHING : TEXT.REFRESH}</span>
            </button>
          </div>

          <DetailedErrorDisplay
            error={error}
            onRetry={handleManualRefresh}
            isRetrying={refreshing}
            retryLabel="重新載入儀表板"
          />
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600 dark:text-gray-400 text-lg">{TEXT.NO_DATA_AVAILABLE}</div>
      </div>
    );
  }

  const overallUsagePercent = stats.totalSpace ? (stats.totalUsed / stats.totalSpace * 100).toFixed(1) : '0';


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 transition-colors">
      <div className="max-w-7xl mx-auto">

        {/* Header with refresh controls */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{TEXT.DASHBOARD}</h1>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {TEXT.LAST_UPDATED}: {getTimeAgo(lastUpdated)}
              </span>
            )}
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? TEXT.REFRESHING : TEXT.REFRESH}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-blue-500" />
              <span className="text-3xl font-bold text-gray-800 dark:text-gray-100">{stats.totalCustomers}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{TEXT.TOTAL_CUSTOMERS}</h3>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Database className="h-8 w-8 text-green-500" />
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">{formatBytes(stats.totalSpace)}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{TEXT.TOTAL_SPACE}</h3>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <HardDrive className="h-8 w-8 text-orange-500" />
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">{formatBytes(stats.totalUsed)}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{TEXT.TOTAL_USED}</h3>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <span className="text-3xl font-bold text-gray-800 dark:text-gray-100">{overallUsagePercent}%</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{TEXT.OVERALL_USAGE}</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">{TEXT.TOP_10_SPACE_USERS}</h2>
            <div className="space-y-3">
              {stats.top10Used.map((customer, index) => (
                <div key={customer.station_code} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-sm text-sm font-medium">
                      #{index + 1}
                    </span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{customer.station_code}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-600 dark:text-gray-300">{formatBytes(customer.total_used)}</span>
                    <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getUsageColor(customer.usage_percent)}`}>
                      {customer.usage_percent}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">{TEXT.TOP_10_NEAR_FULL}</h2>
            <div className="space-y-3">
              {stats.top5NearFull.slice(0, 10).map((customer, index) => (
                <div key={customer.station_code} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300 px-2 py-1 rounded-sm text-sm font-medium">
                      #{index + 1}
                    </span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{customer.station_code}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {formatBytes(customer.available_space || 0)} / {formatBytes(customer.total_space || 0)}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        可用 / 總計
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getUsageColor(customer.usage_percent)}`}>
                      {customer.usage_percent}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <AllCustomersCard />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;